import { type ReactNode } from "react";
import StopIcon from "@mui/icons-material/Stop";
import { Box, Fab, styled } from "@mui/material";
import { customPalette } from "../../theme/palette";

const StopButtonBlue = styled(Fab)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  "&:disabled": {
    backgroundColor: customPalette.gray["400"],
  },
  "&:hover": {
    backgroundColor: theme.palette.primary.main,
  },
}));

const StyledStopIcon = styled(StopIcon)({
  color: customPalette.gray["0"],
});

interface AudioVisualizerProps {
  frequencyLevel: number;
  color: string;
  buttonSize: "medium" | "large";
  buttonWidth: number;
  buttonHeight: number;
  onStop?: () => void;
  disabled?: boolean;
  children?: ReactNode;
  outerPadding?: number;
  innerPadding?: number;
  marginLeft?: number;
  marginRight?: number;
  customMargin?: number | string;
}

export { useAudioAnalysis } from "./useAudioAnalysis";

export function AudioVisualizer({
  frequencyLevel,
  color,
  buttonSize,
  buttonWidth,
  buttonHeight,
  onStop,
  disabled = false,
  children,
  outerPadding = 8,
  innerPadding = 8,
  marginLeft = 4,
  marginRight = 4,
  customMargin,
}: AudioVisualizerProps) {
  const outerOpacity = Math.floor(Math.min(frequencyLevel, 255) * 0.25)
    .toString(16)
    .padStart(2, "0");

  const innerOpacity = Math.floor(Math.min(frequencyLevel, 255) * 0.5)
    .toString(16)
    .padStart(2, "0");

  return (
    <Box marginLeft={marginLeft} marginRight={marginRight}>
      <div
        style={{
          margin: customMargin ?? 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            background: `${color}${outerOpacity}`,
            padding: outerPadding,
            borderRadius: "50%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              background: `${color}${innerOpacity}`,
              padding: innerPadding,
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <StopButtonBlue
              size={buttonSize}
              color="primary"
              sx={{
                width: buttonWidth,
                height: buttonHeight,
                backgroundColor: color,
                "&:hover": {
                  backgroundColor: color,
                },
              }}
              aria-label="Stop Recording"
              onClick={onStop}
              disabled={disabled}
              style={{ boxShadow: "none", transition: "background-color 1s ease-in" }}
            >
              {children || <StyledStopIcon viewBox="3 3 18 18" />}
            </StopButtonBlue>
          </div>
        </div>
      </div>
    </Box>
  );
}
